import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { defer, finalize } from 'rxjs';
import { LoadingService } from '../ui/loading.service';
import { SIN_CARGA } from './contexto';

/**
 * Cuánto puede sostener la barra un solo request, en milisegundos.
 *
 * La barra dice "estoy trayendo lo que pediste", no "hay un request abierto".
 * Un request que tarda más que esto sigue su curso —y la pantalla que lo pidió
 * muestra su propio "Buscando…"—, pero deja de contar para la barra. Sin tope,
 * alcanzaba uno colgado (una conexión que quedó a medias al suspender la
 * máquina o cambiar de red: `fetch` no tiene timeout propio) para que la barra
 * no se apagara nunca más en toda la sesión.
 */
const TOPE_BARRA_MS = 20_000;

/**
 * Cuenta las peticiones en vuelo para la barra de progreso del encabezado.
 *
 * **Cada request descuenta una sola vez**, pase lo que pase primero: que
 * termine, que falle, que lo cancelen (un `switchMap` que pasa al siguiente,
 * un `httpResource` que cambia de parámetros) o que se le venza el tope.
 *
 * **El conteo arranca al suscribirse**, pegado al `finalize`: si algo de la
 * cadena de interceptores tirara una excepción al armar el request, el
 * `finalize` igual corre. Antes el conteo subía antes de `next(req)` y el
 * `finalize` se enganchaba después: esa excepción dejaba el contador en uno
 * para siempre.
 */
export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SIN_CARGA)) return next(req);
  const loading = inject(LoadingService);
  // Un refresco automático no enciende la barra (ver `LoadingService`).
  if (!loading.pedidoPorElUsuario()) return next(req);

  return defer(() => {
    let contado = true;
    const soltar = () => {
      if (!contado) return;
      contado = false;
      clearTimeout(tope);
      loading.terminar();
    };
    loading.arrancar();
    const tope = setTimeout(soltar, TOPE_BARRA_MS);
    // El `defer` de adentro convierte una excepción de `next` en un error del
    // observable, y así también pasa por el `finalize`.
    return defer(() => next(req)).pipe(finalize(soltar));
  });
};
